import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isFreeUser } from './utils/planAccess';
import { ClockIcon, UserGroupIcon, ChatIcon, BriefcaseIcon, HandIcon, RefreshIcon, GlobeAltIcon, ExclamationIcon } from '@heroicons/react/outline';
import { PanelLeft } from "lucide-react";
import { FREE_SCENARIO_IDS } from './utils/freeScenarioIds';

const CATEGORY_ICONS = {
  'Performance & Quality Issues': <ClockIcon className="w-5 h-5 text-purple-700" />,
  'Motivation & Engagement': <UserGroupIcon className="w-5 h-5 text-purple-700" />,
  'Communication Challenges': <ChatIcon className="w-5 h-5 text-purple-700" />,
  'Behavior & Professionalism': <BriefcaseIcon className="w-5 h-5 text-purple-700" />,
  'Collaboration & Team Dynamics': <HandIcon className="w-5 h-5 text-purple-700" />,
  'Adaptability & Change Resistance': <RefreshIcon className="w-5 h-5 text-purple-700" />,
  'New Hire Integration': <GlobeAltIcon className="w-5 h-5 text-purple-700" />,
  'Boundaries': <ExclamationIcon className="w-5 h-5 text-purple-700" />,
};

const FILTER_OPTIONS = [
  { label: 'All Scenarios', value: 'all' },
  { label: 'Featured', value: 'featured' },
  { label: 'New', value: 'new' },
  { label: 'Most Popular', value: 'popular' },
];

const CATEGORY_ORDER = Object.keys(CATEGORY_ICONS);

const difficultyLabel = {
  1: 'Beginner',
  3: 'Intermediate',
  5: 'Advanced',
};

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function ScenarioSelectionNew({ setSelectedScenario }) {
  const [scenarios, setScenarios] = useState([]);
  const [filteredScenarios, setFilteredScenarios] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCategories, setShowCategories] = useState(true);
  const [userGroups, setUserGroups] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showGroupFilters, setShowGroupFilters] = useState(true);
  const navigate = useNavigate();

  // 🔑 Ethos login flag
  const isEthos = localStorage.getItem('isEthosUser') === 'true';

  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/scenarios-new`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch scenarios');
        const data = await response.json();

        console.log("Loaded Scenarios:", data);

        setUserGroups(data.userGroups || []); // ✅ Set the user’s group memberships
        setScenarios(data.scenarios || []);
        setFilteredScenarios(data.scenarios || []);

        const scenarioGroups = Array.from(
          new Set(
            (data.scenarios || [])
              .flatMap(s => s.groups || [])
              .filter(group =>
                group && group !== 'public' && (data.userGroups || []).includes(group)
            )
          )
        );
        
        setAvailableGroups(scenarioGroups);

        // 🔑 Default Ethos filter ONCE if Ethos login and user has access to that group
        if (isEthos) {
          const ethosKey = scenarioGroups.find(g => g.toLowerCase() === 'ethos');
          if (ethosKey) {
            setSelectedGroup(ethosKey); // user can still click to toggle back to 'all'
          }
        }
      } catch (error) {
        console.error('Error fetching scenarios:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchScenarios();
  }, []);

  useEffect(() => {
    const text = searchText.toLowerCase();
  
    const matchesSearch = (s) =>
      s.title.toLowerCase().includes(text) ||
      s.content?.summary?.toLowerCase().includes(text);
  
    const matchesFilter = (s) =>
      selectedFilter === 'all' || s.filters?.includes(selectedFilter);
  
    const matchesDifficulty = (s) =>
      selectedDifficulty === 'all' || String(s.content?.difficulty) === selectedDifficulty;
  
    const matchesCategory = (s) =>
      !selectedCategory || s.tags?.includes(selectedCategory);

    const matchesGroup = (s) =>
      selectedGroup === 'all' || (s.groups || []).includes(selectedGroup);
  
    const filtered = scenarios.filter(
      (s) => matchesSearch(s) && matchesFilter(s) && matchesDifficulty(s) && matchesCategory(s) && matchesGroup(s)
    );
  
    if (isFreeUser()) {
      filtered.sort((a, b) => {
        const aAllowed = FREE_SCENARIO_IDS.includes(a.id);
        const bAllowed = FREE_SCENARIO_IDS.includes(b.id);
        return aAllowed === bAllowed ? 0 : aAllowed ? -1 : 1;
      });
    }
    
    setFilteredScenarios(filtered);
  }, [searchText, selectedFilter, selectedDifficulty, selectedCategory, selectedGroup, scenarios]);

  // Handle category column visibility based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setShowCategories(false);
      } else {
        setShowCategories(true);
      }
    };
  
    // Run once on mount
    handleResize();
  
    // Listen to resize events
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleScenarioSelect = (scenario) => {
    setSelectedScenario(scenario);
  
    const difficulty = Number(scenario?.content?.difficulty || 1); // typically 1 / 3 / 5 in your system
  
    localStorage.setItem('scenarioId', scenario.id);
    localStorage.setItem('difficultyLevel', String(difficulty)); // store as string (localStorage), parse later
  
    navigate('/simulator', {
      state: {
        scenarioId: scenario.id,
        difficultyLevel: difficulty,
        fromPage: true,
      },
    });
  };

  const categoryCounts = CATEGORY_ORDER.reduce((acc, category) => {
    acc[category] = filteredScenarios.filter((s) =>
      s.tags?.includes(category)
    ).length;
    return acc;
  }, {});

  return (
    <div className="flex max-w-7xl min-w-[470px] mx-auto p-6 mt-6 bg-white shadow-lg rounded-lg gap-6">

      {/* Left: Categories */}
      {showCategories && (
      <div className="w-full max-w-[200px] flex-shrink-0 border-r pr-4 sticky top-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Categories</h2>
          <button
            onClick={() => setShowCategories(false)}
            className="block p-1 text-gray-500 hover:text-gray-800"
            aria-label="Close categories"
          >
            <PanelLeft className="w-5 h-5" />
          </button>
        </div>
          <ul className="space-y-1.5">
            {CATEGORY_ORDER.map((category) => (
              <li
                key={category}
                onClick={() =>
                  setSelectedCategory(selectedCategory === category ? null : category)
                }
                className={`flex items-center px-0 py-2 rounded cursor-pointer transition border-l-4 ${
                  selectedCategory === category
                    ? 'border-purple-700 bg-purple-50 text-purple-800 font-semibold'
                    : 'border-transparent text-gray-800 hover:border-purple-300 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-md">
                    {CATEGORY_ICONS[category]}
                  </div>
                  <span className="text-sm font-medium break-words max-w-[70%]">{category}</span>
                </div>
                {/* <span className="text-sm font-semibold ml-2">{categoryCounts[category] || 0}</span> */}
              </li>
            ))}
          </ul>

          {/* Group Scenarios Section */}
          {availableGroups.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => setShowGroupFilters(!showGroupFilters)}
                  className="flex items-center gap-1 text-md font-bold text-gray-700 hover:text-gray-900 transition"
                >
                <svg
                  className={`w-4 h-4 transform transition-transform duration-200 ${
                    showGroupFilters ? 'rotate-90' : ''
                  }`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                  Group Scenarios
                </button>
              </div>
              {showGroupFilters && (
                <ul className="space-y-1">
                  {availableGroups
                    .filter(group => userGroups.includes(group))  // 🔥 Only show if user is in this group
                    .map((group) => (
                      <li
                        key={group}
                        onClick={() =>
                          setSelectedGroup(selectedGroup === group ? 'all' : group)
                        }
                        className={`cursor-pointer text-sm rounded px-2 py-1 transition ${
                          selectedGroup === group
                            ? 'bg-purple-100 text-purple-800 font-semibold'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {group.charAt(0).toUpperCase() + group.slice(1)}
                      </li>
                  ))}
                </ul>
              )}
            </div>
          )}
      </div>
    )}

      {/* Right: Search + Filters + Scenarios */}
      <div className="flex-1 min-w-[390px]">
        <div className="sticky top-0 bg-white z-10 pb-4">
          <div className="flex items-center justify-start gap-4 mb-4">
            {!showCategories && (
              <button
                onClick={() => setShowCategories(true)}
                className="bg-white p-2 rounded shadow text-gray-600 hover:text-gray-800"
                aria-label="Open categories"
              >
                <PanelLeft className="w-5 h-5" />
              </button>
            )}
            <h1 className="text-3xl font-bold">Available Scenarios</h1>
          </div>

          {/* Search bar */}
          <input
            type="text"
            placeholder="Search by keyword, employee name, subject, etc..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full border border-gray-300 px-4 py-2 rounded-lg shadow-sm mb-6"
          />

          {/* Filter Row: Type + Difficulty */}
          <div className="flex flex-wrap items-start gap-4 mb-4 text-sm">
            {/* Type Filters (left aligned with grey background) */}
            <div className="bg-gray-100 p-2 rounded-lg flex gap-2 whitespace-nowrap overflow-auto min-w-[280px] max-w-full">
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedFilter(option.value)}
                  className={`px-3 py-1.5 rounded-md font-medium transition ${
                    selectedFilter === option.value
                      ? 'bg-white text-gray-800 shadow'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Difficulty Dropdown (no grey background) */}
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-700">Difficulty:</span>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              >
                <option value="all">All Levels</option>
                <option value="1">Beginner</option>
                <option value="3">Intermediate</option>
                <option value="5">Advanced</option>
              </select>
            </div>
          </div>
        </div>

        {/* Scenarios Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 overflow-y-auto max-h-screen">
          {filteredScenarios.map((scenario) => {
            const difficulty = difficultyLabel[scenario.content?.difficulty] || 'N/A';
            const tags = scenario.filters || [];
            const isAccessible = !isFreeUser() || FREE_SCENARIO_IDS.includes(scenario.id);

            // Tag priority
            let priorityTag = '';
            if (tags.includes('new')) priorityTag = 'New';
            else if (tags.includes('featured')) priorityTag = 'Featured';
            else if (tags.includes('popular')) priorityTag = 'Most Popular';

            // Category display (up to 2)
            const categories = scenario.tags?.slice(0, 2) || [];

            return (
              <div key={scenario.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex flex-col justify-between">
                {/* Title + Feature Tag */}
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-800">{scenario.title}</h3>
                  {priorityTag && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                      {priorityTag}
                    </span>
                  )}
                </div>

                {/* Categories + Difficulty */}
                <div className="flex flex-wrap items-start text-xs text-gray-600 mb-2">
                  {/* Category container with max width */}
                  <div className="flex flex-wrap gap-1 max-w-[50%]">
                    {categories.map((cat, i) => (
                      <span key={i}>
                        {cat}
                        {i < categories.length - 1 && <span className="mx-1 text-gray-400">|</span>}
                      </span>
                    ))}
                  </div>

                  {/* Difficulty just after categories */}
                  <span className="ml-2 bg-white border border-gray-300 rounded-full px-2 py-0.5 font-semibold text-gray-700 whitespace-nowrap">
                    {difficulty}
                  </span>
                </div>

                {/* Summary */}
                <p className="text-sm text-gray-700 mb-4">
                  {scenario.content?.summary || 'No summary available.'}
                </p>

                {/* Divider */}
                <hr className="border-gray-200 mb-4" />

                {/* Start Button */}
                  {isAccessible ? (
                    <button
                      className="bg-purple-600 text-white text-sm font-semibold py-2 rounded-lg w-full hover:bg-purple-700 transition"
                      onClick={() => handleScenarioSelect(scenario)}
                    >
                      Start Scenario →
                    </button>
                  ) : (
                    <a
                      href="/pricing"
                      className="bg-yellow-100 text-yellow-800 text-sm font-semibold py-2 rounded-lg w-full text-center block hover:bg-yellow-200 transition"
                    >
                      Upgrade for Access →
                    </a>
                  )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ScenarioSelectionNew;
